import { Form, Formik, type FormikErrors, type FormikHelpers } from "formik";
import { toast } from "sonner";
import * as Yup from "yup";
import { FormField } from "@/components/common/form-field";
import { PhoneField, ZipField } from "@/components/common/masked-form-field";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { createEmployer, updateEmployer } from "@/features/clients/api";
import { CheckboxField, SelectField } from "@/features/clients/components/record-form-fields";
import {
  FIELD_LABEL,
  optionalMatch,
  phoneRule,
  zipRule,
} from "@/features/clients/components/profile-form-shared";
import type {
  Employer,
  EmployerBelongsTo,
  EmployerPayload,
  OwnershipInterest,
  PayPeriod,
} from "@/features/clients/types";
import { applyApiErrors } from "@/lib/form-errors";
import { onlyDigits } from "@/lib/masks";

const BELONGS_TO_OPTIONS: { value: EmployerBelongsTo; label: string }[] = [
  { value: "TAXPAYER", label: "Taxpayer" },
  { value: "SPOUSE", label: "Spouse" },
];

const PAY_PERIOD_OPTIONS: { value: Exclude<PayPeriod, "">; label: string }[] = [
  { value: "WEEKLY", label: "Weekly" },
  { value: "BIWEEKLY", label: "Biweekly" },
  { value: "SEMIMONTHLY", label: "Semimonthly" },
  { value: "MONTHLY", label: "Monthly" },
];

const OWNERSHIP_OPTIONS: { value: OwnershipInterest; label: string }[] = [
  { value: "NONE", label: "None" },
  { value: "PARTNER", label: "Partner" },
  { value: "OFFICER", label: "Officer" },
  { value: "SOLE_PROPRIETOR", label: "Sole proprietor" },
];

interface EmployerFormValues {
  belongsTo: EmployerBelongsTo;
  name: string;
  occupation: string;
  street: string;
  city: string;
  state: string;
  zip: string;
  county: string;
  phone: string;
  payPeriod: PayPeriod;
  yearsEmployed: string;
  monthsEmployed: string;
  okToContactAtWork: boolean;
  ownershipInterest: OwnershipInterest;
}

function intRule(max: number, label: string) {
  return Yup.string()
    .matches(/^\d*$/, "Enter a whole number")
    .test("range", `${label} must be between 0 and ${max}`, (v) => {
      if (!v) return true;
      const n = Number(v);
      return n >= 0 && n <= max;
    });
}

const validationSchema = Yup.object({
  name: Yup.string().trim().required("Employer name is required"),
  state: optionalMatch(/^[A-Za-z]{2}$/, "Use a 2-letter state code"),
  zip: zipRule(),
  phone: phoneRule(),
  yearsEmployed: intRule(80, "Years"),
  monthsEmployed: intRule(11, "Months"),
});

function toInitialValues(employer: Employer | null): EmployerFormValues {
  return {
    belongsTo: employer?.belongsTo ?? "TAXPAYER",
    name: employer?.name ?? "",
    occupation: employer?.occupation ?? "",
    street: employer?.street ?? "",
    city: employer?.city ?? "",
    state: employer?.state ?? "",
    zip: employer?.zip ?? "",
    county: employer?.county ?? "",
    phone: employer?.phone ?? "",
    payPeriod: employer?.payPeriod ?? "",
    yearsEmployed: employer?.yearsEmployed != null ? String(employer.yearsEmployed) : "",
    monthsEmployed: employer?.monthsEmployed != null ? String(employer.monthsEmployed) : "",
    okToContactAtWork: employer?.okToContactAtWork ?? false,
    ownershipInterest: employer?.ownershipInterest ?? "NONE",
  };
}

function toPayload(v: EmployerFormValues): EmployerPayload {
  return {
    belongs_to: v.belongsTo,
    name: v.name.trim(),
    occupation: v.occupation,
    street: v.street,
    city: v.city,
    state: v.state.toUpperCase(),
    zip: onlyDigits(v.zip),
    county: v.county,
    phone: onlyDigits(v.phone),
    pay_period: v.payPeriod,
    years_employed: v.yearsEmployed === "" ? null : Number(v.yearsEmployed),
    months_employed: v.monthsEmployed === "" ? null : Number(v.monthsEmployed),
    ok_to_contact_at_work: v.okToContactAtWork,
    ownership_interest: v.ownershipInterest,
  };
}

interface EmployerFormDialogProps {
  clientId: string;
  /** `null` = add; otherwise the dialog edits this employer. */
  employer: Employer | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Fires after a successful save so the section can refetch. */
  onSaved: () => void;
}

export function EmployerFormDialog({
  clientId,
  employer,
  open,
  onOpenChange,
  onSaved,
}: EmployerFormDialogProps) {
  const editing = employer !== null;

  async function handleSubmit(values: EmployerFormValues, bag: FormikHelpers<EmployerFormValues>) {
    try {
      if (editing) {
        await updateEmployer(clientId, employer.id, toPayload(values));
      } else {
        await createEmployer(clientId, toPayload(values));
      }
      toast.success(editing ? "Employer updated" : "Employer added");
      onSaved();
      onOpenChange(false);
    } catch (err) {
      const setErrors = bag.setErrors as (errors: FormikErrors<never>) => void;
      toast.error(applyApiErrors(err, { setErrors }, { fallback: "Couldn't save employer." }));
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit Employer" : "Add Employer"}</DialogTitle>
        </DialogHeader>
        <Formik
          initialValues={toInitialValues(employer)}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ isSubmitting }) => (
            <Form className="grid gap-4">
              <div className="grid grid-cols-2 gap-4">
                <SelectField name="belongsTo" label="Employer of" options={BELONGS_TO_OPTIONS} />
                <SelectField
                  name="ownershipInterest"
                  label="Ownership Interest"
                  options={OWNERSHIP_OPTIONS}
                />
              </div>
              <FormField name="name" label="Employer Name" labelClassName={FIELD_LABEL} />
              <div className="grid grid-cols-2 gap-4">
                <FormField name="occupation" label="Occupation" labelClassName={FIELD_LABEL} />
                <PhoneField name="phone" label="Phone" labelClassName={FIELD_LABEL} />
              </div>
              <FormField name="street" label="Street Address" labelClassName={FIELD_LABEL} />
              <div className="grid grid-cols-2 gap-4">
                <FormField name="city" label="City" labelClassName={FIELD_LABEL} />
                <FormField name="county" label="County" labelClassName={FIELD_LABEL} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  name="state"
                  label="State"
                  labelClassName={FIELD_LABEL}
                  maxLength={2}
                  placeholder="CA"
                />
                <ZipField name="zip" label="ZIP Code" labelClassName={FIELD_LABEL} />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <SelectField name="payPeriod" label="Pay Period" options={PAY_PERIOD_OPTIONS} />
                <FormField
                  name="yearsEmployed"
                  label="Years There"
                  labelClassName={FIELD_LABEL}
                  inputMode="numeric"
                />
                <FormField
                  name="monthsEmployed"
                  label="+ Months"
                  labelClassName={FIELD_LABEL}
                  inputMode="numeric"
                />
              </div>
              <CheckboxField name="okToContactAtWork" label="OK to contact at work" />
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {editing ? "Save Changes" : "Add Employer"}
                </Button>
              </DialogFooter>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
}
